import React from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"

import { NzDataGrid } from "@/components/nzDataGrid"
import { PageCard } from "@/components/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId } from "@/contexts"
import { useConfirmDeleteBill } from "@/modules/admin/bills"
import { AdminEntityLink } from "@/modules/admin/components/admin-entity-link"
import {
  createAdminGridDeleteAction,
  createAdminGridEditAction,
  createAdminGridViewAction,
  useAdminGridActionsColumn,
} from "@/modules/admin/components/admin-grid-actions-column"
import {
  CORE_API_STRING_FILTER_OPERATOR_KEYS,
  DEFAULT_PAGE_SIZE,
  DataGridFill,
  DataGridFilterPanel,
  DataGridFixedLogicOperatorInput,
  stringFilterItemsFromUrlValues,
  useDataGridVirtualScrollListener,
  useGridUrlState,
  useInfiniteScroll,
  useServerMappedStringFilters,
} from "@/nzds/data-grid"
import type { Option } from "@/nzds/inputs/inputs.types"
import {
  billToGridRecord,
  billsGridFieldToSortParam,
  billsSortParamToSortModel,
  buildMeterLookupById,
  defaultBillsSortModel,
  getUniqueBillMeterIds,
} from "@/services/bill/bill-grid-utils"
import {
  buildResourceFilterOptions,
  getDistinctMeterResourceCodes,
} from "@/services/bill/bill-resource"
import type { BillGridRecord } from "@/services/bill/bill.types"
import { buildBillsExportPath } from "@/services/bill/bills-export-path"
import { useBillsInfinite } from "@/services/bill/use-bills-infinite"
import type { BillTextFilterQueryOptions } from "@/services/core-api/types"
import { useAllMeters, useMeters } from "@/services/meter"
import { useCommonBillColumns } from "@/utils/bill/use-common-bill-columns"
import { EMPTY_DATA_PLACEHOLDER } from "@/utils/constants"
import { usePeriodColumn } from "@/utils/grid-columns/use-period-column"
import { useResourceColumn } from "@/utils/grid-columns/use-resource-column"
import { useUrl } from "@/utils/url"

import { Stack } from "@mui/material"
import type {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSlotProps,
  GridSlots,
  GridSortModel,
} from "@mui/x-data-grid-pro"
import { getGridSingleSelectOperators } from "@mui/x-data-grid-pro"

import { AdminBillsGridToolbar } from "./admin-bills-grid-toolbar"

const ADMIN_BILLS_FILTER_DEBOUNCE_MS = 300
const RESOURCE_FILTER_FIELD = "resource_type"
const DEFAULT_BILLS_SORT_PARAM = "start_date:desc"

const adminBillsStringFields = ["meter_code", "site_name"] as const

type AdminBillsStringField = (typeof adminBillsStringFields)[number]

const adminBillsStringFieldToApiField: Record<AdminBillsStringField, string> = {
  meter_code: "meter_code",
  site_name: "site_name",
}

const ADMIN_BILLS_STRING_FILTER_URL_KEYS = [
  "meter_code_contains",
  "meter_code_empty",
  "meter_code_ends_with",
  "meter_code_equals",
  "meter_code_not_contains",
  "meter_code_starts_with",
  "site_name_contains",
  "site_name_empty",
  "site_name_ends_with",
  "site_name_equals",
  "site_name_not_contains",
  "site_name_starts_with",
] as const satisfies readonly (keyof BillTextFilterQueryOptions)[]

const adminBillsGridUrlKeys = [
  ...ADMIN_BILLS_STRING_FILTER_URL_KEYS,
  RESOURCE_FILTER_FIELD,
  "sort",
] as const

const resourceEqualsOperators = getGridSingleSelectOperators().filter(
  (operator) => operator.value === "is"
)

const adminBillsFilterPanelSlotProps: Pick<
  GridSlotProps["filterPanel"],
  "filterFormProps"
> = {
  filterFormProps: {
    logicOperatorInputProps: {
      as: DataGridFixedLogicOperatorInput,
    },
  },
}

interface ExportableBillsCheck {
  csvDownloadHref: string | undefined
  isError: boolean
  isQueryLoading: boolean
  total: number
}

const hasExportableBills = ({
  csvDownloadHref,
  isError,
  isQueryLoading,
  total,
}: ExportableBillsCheck): boolean =>
  Boolean(csvDownloadHref) && !isQueryLoading && !isError && total > 0

const buildBillsSortParam = (sortModel: GridSortModel): string => {
  const firstSort = sortModel[0]

  if (!firstSort?.field || !firstSort.sort) {
    return DEFAULT_BILLS_SORT_PARAM
  }

  return (
    billsGridFieldToSortParam(firstSort.field, firstSort.sort) ??
    DEFAULT_BILLS_SORT_PARAM
  )
}

const buildAdminBillsFilterModel = (
  values: Record<string, string | null>
): GridFilterModel => {
  const items = adminBillsStringFields.flatMap((field) =>
    stringFilterItemsFromUrlValues(
      field,
      values,
      CORE_API_STRING_FILTER_OPERATOR_KEYS
    )
  )
  const resourceType = values[RESOURCE_FILTER_FIELD]

  if (resourceType) {
    items.push({
      field: RESOURCE_FILTER_FIELD,
      id: RESOURCE_FILTER_FIELD,
      operator: "is",
      value: resourceType,
    })
  }

  return { items }
}

const withSingleResourceFilter = (model: GridFilterModel): GridFilterModel => {
  const resourceItems = model.items.filter(
    (item) => item.field === RESOURCE_FILTER_FIELD
  )
  const kept = resourceItems[resourceItems.length - 1]

  if (!kept || resourceItems.length <= 1) {
    return model
  }

  return {
    ...model,
    items: model.items.filter(
      (item) => item.field !== RESOURCE_FILTER_FIELD || item === kept
    ),
  }
}

const getResourceFilterValue = (model: GridFilterModel): string | null => {
  const resourceItem = [...model.items]
    .reverse()
    .find((item) => item.field === RESOURCE_FILTER_FIELD)

  if (typeof resourceItem?.value !== "string") {
    return null
  }

  const value = resourceItem.value.trim()
  return value || null
}

const toBillStringFilterQuery = (
  whereClause: Record<string, unknown>
): BillTextFilterQueryOptions => {
  const query: BillTextFilterQueryOptions = {}

  ADMIN_BILLS_STRING_FILTER_URL_KEYS.forEach((key) => {
    const value = whereClause[key]
    if (typeof value === "boolean" || typeof value === "string") {
      Object.assign(query, { [key]: value })
    }
  })

  return query
}

const toStringFilterUrlValue = (value: unknown): string | null => {
  if (typeof value === "string" && value.length > 0) {
    return value
  }

  if (typeof value === "boolean") {
    return String(value)
  }

  return null
}

const buildAdminBillsFilterUrlUpdates = (
  whereClause: Record<string, unknown>,
  resourceType: string | null
): Record<string, string | null> => {
  const updates: Record<string, string | null> = {
    [RESOURCE_FILTER_FIELD]: resourceType,
  }

  ADMIN_BILLS_STRING_FILTER_URL_KEYS.forEach((key) => {
    updates[key] = toStringFilterUrlValue(whereClause[key])
  })

  return updates
}

export const AdminBillsRoute: React.FC = () => {
  const { activeOrganizationId } = useActiveOrganizationId()

  if (!activeOrganizationId) {
    return null
  }

  return (
    <AdminBillsRouteContent
      /**
       * The key is set to the organization ID to force a re-render when the
       * organization ID changes.
       */
      key={activeOrganizationId}
      organizationId={activeOrganizationId}
    />
  )
}

interface AdminBillsRouteContentProps {
  organizationId: string
}

const AdminBillsRouteContent: React.FC<AdminBillsRouteContentProps> = ({
  organizationId,
}) => {
  const { t } = useTranslation("common")
  const { search } = useLocation()
  const { adminBillEditPath, adminBillPath, adminMeterPath, adminSitePath } =
    useUrl()
  const gridRootRef = React.useRef<HTMLDivElement | null>(null)
  const { replaceGridUrlState, values: gridUrlValues } = useGridUrlState(
    adminBillsGridUrlKeys
  )
  const [initialFilterModel] = React.useState(() =>
    buildAdminBillsFilterModel(gridUrlValues)
  )
  const { filterModel, setFilterModel, whereClause } =
    useServerMappedStringFilters({
      debounceMs: ADMIN_BILLS_FILTER_DEBOUNCE_MS,
      fieldToApiField: adminBillsStringFieldToApiField,
      fields: adminBillsStringFields,
      initialFilterModel,
      mode: "multiple",
      operatorKeys: CORE_API_STRING_FILTER_OPERATOR_KEYS,
    })
  const appliedResource = getResourceFilterValue(filterModel)
  const [requestedSort, setRequestedSort] = React.useState<string | null>(null)
  const sortModel = billsSortParamToSortModel(gridUrlValues.sort)
  const sortParam = buildBillsSortParam(sortModel)
  const stringFilters = toBillStringFilterQuery(whereClause)

  const { metersData: organizationMeters, metersIsComplete } = useAllMeters({
    apiQueryOptions: {
      includes: ["meter_source_type"],
      where: { organization_id: organizationId },
    },
  })
  const resourceOptions = buildResourceFilterOptions(
    getDistinctMeterResourceCodes(
      metersIsComplete ? (organizationMeters ?? []) : []
    ),
    (key) => t(key)
  )
  const resourceValueOptions = resourceOptions.filter(
    (option): option is Option & { value: string } =>
      typeof option.value === "string"
  )

  const listQuerySignature = JSON.stringify({
    resourceType: appliedResource,
    sort: sortParam,
    where: stringFilters,
  })

  const {
    billsData,
    billsFetchNextPage,
    billsHasNextPage,
    billsIsError,
    billsIsFetching,
    billsIsFetchingNextPage,
    billsIsLoading,
    billsIsPlaceholderData,
  } = useBillsInfinite({
    apiQueryOptions: {
      is_current: true,
      limit: DEFAULT_PAGE_SIZE,
      sort: sortParam,
      ...stringFilters,
      ...(appliedResource ? { resource_type: appliedResource } : {}),
    },
    organizationId,
  })

  const meterIds = getUniqueBillMeterIds(billsData?.pages)

  const { metersData, metersIsPlaceholderData } = useMeters({
    apiQueryOptions: {
      includes: ["site"],
      pageSize: meterIds.length,
      where: {
        id: meterIds,
        organization_id: organizationId,
      },
    },
    queryOptions: {
      enabled: meterIds.length > 0,
    },
  })

  const meterLookupById = buildMeterLookupById(
    metersData,
    metersIsPlaceholderData
  )

  const rows = billsData
    ? billsData.pages
        .flatMap((page) => page.items)
        .map((bill) => billToGridRecord(bill, meterLookupById))
    : []

  const { confirmAndDeleteBill, deleteBillIsPending } = useConfirmDeleteBill({
    organizationId,
  })

  const handleDeleteClick = (
    bill: Pick<BillGridRecord, "id" | "is_locked">
  ) => {
    if (bill.is_locked) {
      return
    }

    confirmAndDeleteBill(bill.id).catch(() => undefined)
  }

  const commonBillColumns = useCommonBillColumns()
  const periodColumn = usePeriodColumn<BillGridRecord>()
  const resourceColumn = useResourceColumn<BillGridRecord>()
  const billsGridColumns: GridColDef<BillGridRecord>[] = [
    {
      ...commonBillColumns.siteName,
      filterable: true,
      renderCell: ({
        row,
        value,
      }: GridRenderCellParams<BillGridRecord, string>) => (
        <AdminEntityLink
          label={value ?? ""}
          to={
            row.site_id && value !== EMPTY_DATA_PLACEHOLDER
              ? adminSitePath(row.site_id)
              : undefined
          }
        />
      ),
    },
    {
      ...commonBillColumns.meterCode,
      filterable: true,
      renderCell: ({
        row,
        value,
      }: GridRenderCellParams<BillGridRecord, string>) => (
        <AdminEntityLink
          label={value ?? ""}
          to={
            row.meter_id != null && value !== EMPTY_DATA_PLACEHOLDER
              ? adminMeterPath(String(row.meter_id))
              : undefined
          }
        />
      ),
    },
    {
      ...resourceColumn,
      filterOperators: resourceEqualsOperators,
      filterable: true,
      type: "singleSelect",
      valueOptions: resourceValueOptions,
    },
    periodColumn,
    commonBillColumns.usage,
    commonBillColumns.cost,
    commonBillColumns.flowDirection,
  ]
  const actionsColumn = useAdminGridActionsColumn<BillGridRecord>({
    getActions: ({ row }) => {
      const billId = String(row.id)
      return [
        createAdminGridViewAction(
          `${adminBillPath(billId)}${search}`,
          t("button.view")
        ),
        createAdminGridEditAction(
          `${adminBillEditPath(billId)}${search}`,
          t("button.edit")
        ),
        createAdminGridDeleteAction({
          disabled: row.is_locked || deleteBillIsPending,
          label: t("button.delete"),
          onDeleteClick: () => {
            handleDeleteClick(row)
          },
        }),
      ]
    },
    minWidth: 160,
    width: 160,
  })
  const columns: GridColDef<BillGridRecord>[] = [
    ...billsGridColumns,
    { ...actionsColumn, filterable: false },
  ]

  const { handleOnRowsScrollEnd, handleOnScrollTopChange } = useInfiniteScroll({
    fetchNextPage: billsFetchNextPage,
    hasNextPage: billsHasNextPage,
    isEnabled: true,
    isFetchingNextPage: billsIsFetchingNextPage,
  })

  useDataGridVirtualScrollListener({
    gridRootRef,
    onScroll: handleOnScrollTopChange,
  })

  const previousListQuerySignatureRef = React.useRef(listQuerySignature)

  React.useEffect(() => {
    if (previousListQuerySignatureRef.current === listQuerySignature) {
      return
    }

    previousListQuerySignatureRef.current = listQuerySignature

    const virtualScroller = gridRootRef.current?.querySelector(
      ".MuiDataGrid-virtualScroller"
    )
    if (virtualScroller instanceof HTMLElement) {
      virtualScroller.scrollTop = 0
    }
  }, [listQuerySignature])

  React.useEffect(() => {
    replaceGridUrlState(
      buildAdminBillsFilterUrlUpdates(whereClause, appliedResource)
    )
  }, [appliedResource, replaceGridUrlState, whereClause])

  const handleFilterModelChange = (nextFilterModel: GridFilterModel) => {
    setFilterModel(withSingleResourceFilter(nextFilterModel))
  }

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    const nextSortModel = newSortModel.length
      ? newSortModel
      : defaultBillsSortModel
    const nextSort = buildBillsSortParam(nextSortModel)

    if (nextSort !== sortParam) {
      setRequestedSort(nextSort)
    }

    replaceGridUrlState({
      sort: nextSort,
    })
  }

  const isSortSettling = requestedSort != null && requestedSort !== sortParam
  const isQueryLoading =
    billsIsPlaceholderData ||
    isSortSettling ||
    (billsIsFetching && !billsIsFetchingNextPage)
  const isRecordCountLoading = billsIsPlaceholderData || billsIsLoading

  const csvDownloadHref = buildBillsExportPath(organizationId, {
    resourceType: appliedResource ?? undefined,
    sort: sortParam,
    stringFilters,
  })
  const excelDownloadHref = buildBillsExportPath(organizationId, {
    format: "xlsx",
    resourceType: appliedResource ?? undefined,
    sort: sortParam,
    stringFilters,
  })
  const canDownload = hasExportableBills({
    csvDownloadHref,
    isError: billsIsError,
    isQueryLoading,
    total: billsData?.pages[0]?.total ?? 0,
  })

  return (
    <Stack sx={{ gap: 2, height: "100%", minHeight: 0 }}>
      <PageTitle heading={t("admin.bills")} />
      <PageCard
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <DataGridFill ref={gridRootRef}>
          <NzDataGrid
            aria-label={t("admin.billsGrid.ariaLabel")}
            columns={columns}
            filterMode="server"
            filterModel={filterModel}
            getRowId={(row: BillGridRecord) => row.id}
            loading={
              billsIsLoading ||
              billsIsFetchingNextPage ||
              (billsIsFetching && rows.length > 0)
            }
            onFilterModelChange={handleFilterModelChange}
            onRowsScrollEnd={handleOnRowsScrollEnd}
            onSortModelChange={handleSortModelChange}
            initialState={{ pinnedColumns: { right: ["actions"] } }}
            rows={rows}
            slotProps={{
              filterPanel: adminBillsFilterPanelSlotProps,
              toolbar: {
                canDownload,
                csvDownloadHref,
                excelDownloadHref,
                isRecordCountLoading,
                recordCount: billsData?.pages[0]?.total,
              } as GridSlotProps["toolbar"],
            }}
            slots={{
              filterPanel: DataGridFilterPanel as GridSlots["filterPanel"],
              toolbar: AdminBillsGridToolbar as GridSlots["toolbar"],
            }}
            sortModel={sortModel}
            sortingMode="server"
          />
        </DataGridFill>
      </PageCard>
    </Stack>
  )
}
